import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { QuestionType } from '@/types'
import { InlinePlacement } from '@/api/surveyBuilderApi'
import { ChannelConfig } from '@/api/surveySettingApi'
import { logoutUser, switchContext } from './authSlice'

interface Option {
    id: string
    text: string
}

interface QuestionConfig {
    options?: Option[]
    scale?: number
    maxLabel?: string
    minLabel?: string
    displayType?: string
    emojiImages?: string[] // Array of emoji image paths
    placeholder?: string
    randomize?: boolean
    allowOther?: boolean
    otherLabel?: string
    maxSelect?: number | null
    minSelect?: number | null
    nextQuestionId?: string | null
    isTerminal?: boolean
    hideLabel?: boolean
}

interface InlineImage {
    altText: string
    imageId: string
    imageUrl: string
    thumbnailUrl: string
    placement: InlinePlacement
    metadata: {
        size: number
        width: number
        height: number
        format: string
    }
}

interface ContentBlockData {
    contentBlockId: string
    surveyId: string
    merchantId: string
    storeId: string
    questionId: string
    orderIndex: number
    blocks: Array<{
        blockId: string
        content: {
            heading?: string
            headingLevel?: number
            text?: string
            altText?: string
            caption?: string
            imageId?: string
            imageUrl?: string
            // CTA button fields (used by "cta-button" blocks)
            link?: string
            // Legacy button naming (used by some preview components)
            buttonLabel?: string
            buttonUrl?: string
            redirectionLink?: string
            // Coupon code fields (used by "coupon_code" blocks)
            code?: string
            description?: string
            expiryDate?: string
            termsLink?: string
        }
        blockType:
        | 'heading'
        | 'text'
        | 'image'
        | 'cta-button'
        | 'button'
        | 'banner-image'
        | 'coupon-code'
        orderIndex: number
    }>
    layout: string
    isVisible: boolean
    status: string
    deletedAt: string | null
    createdAt: string
    updatedAt: string
}

interface CardBlock {
    blockId: string
    content: {
        heading?: string
        headingLevel?: number
        text?: string
        altText?: string
        caption?: string
        imageId?: string
        imageUrl?: string
        buttonText?: string
        buttonAction?: string
        buttonUrl?: string
        // Coupon code support on cards (e.g. thank you page)
        code?: string
        description?: string
        expiryDate?: string
        termsLink?: string
    }
    blockType:
    | 'heading'
    | 'text'
    | 'image'
    | 'button'
    | 'banner'
    | 'banner-image'
    | 'coupon-code'
    orderIndex: number
}

export interface CardData {
    cardId: string
    surveyId: string
    cardType: 'welcome' | 'thankYou'
    messageType: 'default' | 'custom'
    blocks: CardBlock[]
    layout: string
    isVisible: boolean
    status: string
    createdAt: string
    updatedAt: string
}

interface Question {
    questionId: string
    surveyId: string
    text: string
    description: string | null
    type:
    | 'single_select'
    | 'multi_select'
    | 'nps'
    | 'rating'
    | 'text'
    | 'date'
    | 'phone'
    | 'drop_down'
    | 'image'
    | 'content_block'
    isRequired: boolean
    isVisible: boolean
    orderIndex: number
    config: QuestionConfig
    flowEnabled: boolean
    //   validation: Record<string, any>;
    logic: Record<string, any>
    inlineImage: InlineImage | null
    contentBlocks?: ContentBlockData
    contentBlockId?: string
}

interface BuilderState {
    surveyId: string | null
    name: string
    title: string
    description: string
    questions: Question[] | []
    surveyRules: any[] | []
    selectedQuestionId: string | null
    currentQuestionIndex: number
    previewResponses: Record<
        string,
        {
            optionId?: string
            optionIds?: string[]
            otherValue?: string
            value?: any
        }
    >
    questionPath: Record<string, string | null>
    status: 'draft' | 'active'
    newQuestionLoading: boolean
    isTemplate: boolean
    templateId: string | null
    channelData: ChannelConfig
    welcomeCard: CardData | null
    thankYouCard: CardData | null
    publicToken: string | null
    qrCodeUrl: string | null
    samplingRate: number | null
    responseLimit: boolean | null
    layout: string
    whatsAppPreview: {
        selectedTemplate: any | null
        variableMapping: Record<number, string>
        availableVariables: Array<{ key: string; label: string; example: string }>
    }
}

const initialState: BuilderState = {
    surveyId: null,
    name: 'Untitled Survey',
    title: '',
    description: '',
    questions: [],
    surveyRules: [],
    selectedQuestionId: null,
    currentQuestionIndex: 0,
    previewResponses: {},
    questionPath: {},
    status: 'draft',
    newQuestionLoading: false,
    isTemplate: false,
    templateId: null,
    channelData: {
        pps: {
            channel: 'pps',
            distributionMethod: 'app-block',
            enabled: true,
        },
        web: {
            channel: 'web',
            distributionMethod: 'link',
            enabled: false,
        },
        whatsapp: {
            channel: 'whatsapp',
            distributionMethod: 'nps',
            enabled: false,
        },
    },
    welcomeCard: null,
    thankYouCard: null,
    publicToken: null,
    qrCodeUrl: null,
    samplingRate: null,
    responseLimit: null,
    layout: 'all_at_once',
    whatsAppPreview: {
        selectedTemplate: null,
        variableMapping: {},
        availableVariables: [],
    },
}

const getDefaultConfigForType = (
    type: QuestionType,
    customConfig?: Partial<QuestionConfig>
): QuestionConfig => {
    switch (type) {
        case 'single_select':
            return {
                options: customConfig?.options || [],
                randomize: true,
                allowOther: false,
                otherLabel: 'Other',
                displayType: 'radio',
                nextQuestionId: null,
                isTerminal: false,
            }

        case 'multi_select':
            return {
                options: customConfig?.options || [],
                maxSelect: null,
                minSelect: 1,
                randomize: true,
                allowOther: false,
                otherLabel: 'Other',
                displayType: 'checkbox',
                nextQuestionId: null,
                isTerminal: false,
            }
        case 'text':
            return {
                placeholder: '',
                nextQuestionId: null,
                isTerminal: false,
            }
        case 'rating':
            return {
                scale: 5,
                maxLabel: '',
                minLabel: '',
                displayType: 'number',
                nextQuestionId: null,
                isTerminal: false,
            }
        case 'nps':
            return {
                scale: 10,
                maxLabel: '',
                minLabel: '',
                displayType: 'number',
                nextQuestionId: null,
            }
        case 'drop_down':
            return {
                options: customConfig?.options || [],
                randomize: true,
                allowOther: false,
                otherLabel: 'Other',
                nextQuestionId: null,
                isTerminal: false,
            }
        case 'date':
            return {
                nextQuestionId: null,
                isTerminal: false,
            }
        case 'image':
            return {
                options: customConfig?.options || [],
                nextQuestionId: null,
                randomize: true,
                isTerminal: false,
                hideLabel: false,
            }
        case 'content_block':
            return {}
        default:
            return {}
    }
}

const builderSlice = createSlice({
    name: 'builder',
    initialState,
    reducers: {
        resetBuilder: (state) => {
            state.surveyId = null
            state.name = 'Untitled Survey'
            state.title = ''
            state.description = ''
            state.questions = []
            state.selectedQuestionId = null
            state.currentQuestionIndex = 0
            state.previewResponses = {}
            state.questionPath = {}
            state.isTemplate = false
            state.templateId = null
            state.channelData = {
                pps: {
                    channel: 'pps',
                    distributionMethod: 'app-block',
                    enabled: false,
                },
                web: {
                    channel: 'web',
                    distributionMethod: 'link',
                    enabled: false,
                },
                whatsapp: {
                    channel: 'whatsapp',
                    distributionMethod: 'nps',
                    enabled: false,
                },
            }
            state.welcomeCard = null
            state.thankYouCard = null
            state.publicToken = null
            state.qrCodeUrl = null
            state.samplingRate = null
            state.responseLimit = null
            state.whatsAppPreview = {
                selectedTemplate: null,
                variableMapping: {},
                availableVariables: [],
            }
        },
        // Update survey id
        updateSurveyId: (state, action: PayloadAction<string>) => {
            state.surveyId = action.payload
        },

        // Update survey title
        updateSurveyName: (state, action: PayloadAction<string>) => {
            state.name = action.payload
        },
        updateSurveyTitle: (state, action: PayloadAction<string>) => {
            state.title = action.payload
        },
        updateSurveyDescription: (state, action: PayloadAction<string>) => {
            state.description = action.payload
        },
        // Set template mode
        setTemplateMode: (
            state,
            action: PayloadAction<{ templateId: string; name?: string }>
        ) => {
            state.isTemplate = true
            state.templateId = action.payload.templateId
            state.surveyId = action.payload.templateId // Use templateId as surveyId for API compatibility
            if (action.payload.name) {
                state.name = action.payload.name
            }
        },
        // Clear template mode
        clearTemplateMode: (state) => {
            state.isTemplate = false
            state.templateId = null
        },

        //Update all questions
        updateSurveyDetails: (
            state,
            action: PayloadAction<{
                questions: Question[]
                name: string
                surveyRules: any[]
                status: 'draft' | 'active'
                title: string
                description: string
                channelData: ChannelConfig
                welcomeCard?: CardData | null
                thankYouCard?: CardData | null
                publicToken?: string | null
                qrCodeUrl?: string | null
                samplingRate?: number | null
                responseLimit?: boolean | null
                layout?: string
            }>
        ) => {
            state.questions = action.payload.questions
            state.name = action.payload.name
            state.status = action.payload.status
            state.surveyRules = action.payload.surveyRules
            state.title = action.payload.title
            state.description = action.payload.description
            state.channelData = action.payload.channelData

            // Update cards if provided
            if (action.payload.welcomeCard !== undefined) {
                state.welcomeCard = action.payload.welcomeCard
            }
            if (action.payload.thankYouCard !== undefined) {
                state.thankYouCard = action.payload.thankYouCard
            }

            // Update public token and QR code URL if provided
            if (action.payload.publicToken !== undefined) {
                state.publicToken = action.payload.publicToken
            }
            if (action.payload.qrCodeUrl !== undefined) {
                state.qrCodeUrl = action.payload.qrCodeUrl
            }

            // Update sampling rate and response limit if provided
            if (action.payload.samplingRate !== undefined) {
                state.samplingRate = action.payload.samplingRate
            }
            if (action.payload.responseLimit !== undefined) {
                state.responseLimit = action.payload.responseLimit
            }
            if (action.payload.layout !== undefined) {
                state.layout = action.payload.layout
            }

            if (action.payload.questions.length > 0) {
                state.selectedQuestionId =
                    action.payload.questions[0].questionId
                state.questionPath = {
                    [action.payload.questions[0].questionId]: null,
                }
            } else {
                state.selectedQuestionId = null
            }
        },
        updateQuestions: (
            state,
            action: PayloadAction<{ questions: Question[] }>
        ) => {
            state.questions = action.payload.questions
        },

        // Add new question
        addQuestion: (
            state,
            action: PayloadAction<{
                type: QuestionType
                position?: number
                question: Question
            }>
        ) => {
            const { question, type, position } = action.payload
            const insertIndex =
                position !== undefined ? position : state.questions.length
            if (!state.surveyId) {
                throw new Error('Survey ID is not set')
            }
            const newQuestion: Question = {
                questionId: question.questionId,
                surveyId: state.surveyId,
                text: ``,
                description: null,
                type: type,
                flowEnabled: false,
                isRequired: false,
                isVisible: true,
                orderIndex: insertIndex,
                config: {
                    ...getDefaultConfigForType(type, question.config),
                },
                ...(type === 'content_block'
                    ? { contentBlocks: question.contentBlocks }
                    : {}),
                ...(type === 'content_block'
                    ? { contentBlockId: question.contentBlockId }
                    : ''),
                inlineImage: null,
                logic: {},
            }

            // Insert question at specified position or at the end
            state.questions.splice(insertIndex, 0, newQuestion)

            state.selectedQuestionId = newQuestion.questionId
            state.currentQuestionIndex = insertIndex
        },

        // Update question text
        updateQuestionText: (
            state,
            action: PayloadAction<{ questionId: string; text: string }>
        ) => {
            const question = state.questions.find(
                (q) => q.questionId === action.payload.questionId
            )
            if (question) {
                question.text = action.payload.text
            }
        },
        updateQuestionDescription: (
            state,
            action: PayloadAction<{ questionId: string; description: string }>
        ) => {
            const question = state.questions.find(
                (q) => q.questionId === action.payload.questionId
            )
            if (question) {
                question.description = action.payload.description
            }
        },

        // Update question config
        updateQuestionConfig: (
            state,
            action: PayloadAction<{
                questionId: string
                config: Partial<QuestionConfig>
                flowEnabled?: boolean
            }>
        ) => {
            const question = state.questions.find(
                (q) => q.questionId === action.payload.questionId
            )
            if (question) {
                const newConfig = {
                    ...question.config,
                    ...action.payload.config,
                }

                // Check for duplicate options (case insensitive)
                if (newConfig.options && newConfig.options.length > 0) {
                    const lowerCaseTexts = new Set()
                    const hasDuplicates = newConfig.options.some((option) => {
                        const lowerText = option.text.toLowerCase().trim()
                        if (lowerText && lowerCaseTexts.has(lowerText)) {
                            return true
                        }
                        lowerCaseTexts.add(lowerText)
                        return false
                    })

                    // If duplicates found, don't update the options
                    if (hasDuplicates) {
                        // Keep original options if duplicates are found
                        newConfig.options = question.config.options
                    }
                }

                question.config = newConfig
            }
        },

        enableFlow: (
            state,
            action: PayloadAction<{ questionId: string; flowEnabled: boolean }>
        ) => {
            const question = state.questions.find(
                (q) => q.questionId === action.payload.questionId
            )
            if (question) {
                question.flowEnabled = action.payload.flowEnabled
            }
        },
        updateSurveyLayoutState: (state, action: PayloadAction<string>) => {
            state.layout = action.payload
        },

        // Delete question
        deleteQuestion: (state, action: PayloadAction<string>) => {
            const index = state.questions.findIndex(
                (q) => q.questionId === action.payload
            )

            if (index !== -1) {
                state.questions.splice(index, 1)
                // Update orderIndex for remaining questions
                state.questions.forEach((q, i) => {
                    q.orderIndex = i
                })

                // Set selectedQuestionId based on position
                if (state.questions.length > 0) {
                    // If we deleted the last question, select the previous one
                    if (index >= state.questions.length) {
                        state.selectedQuestionId =
                            state.questions[index - 1].questionId
                    }
                    // Otherwise select the question at the same position (which is now the next one)
                    else {
                        state.selectedQuestionId =
                            state.questions[index].questionId
                    }

                    // Update currentQuestionIndex to match the new selection
                    state.currentQuestionIndex = state.questions.findIndex(
                        (q) => q.questionId === state.selectedQuestionId
                    )
                } else {
                    // No questions left
                    state.selectedQuestionId = null
                    state.currentQuestionIndex = 0
                }
            }
        },

        // Duplicate question
        duplicateQuestion: (
            state,
            action: PayloadAction<{
                question: Question
            }>
        ) => {
            const { question } = action.payload

            if (question) {
                const index = state.questions.findIndex(
                    (q) => q.questionId === question.questionId
                )

                // Insert the duplicated question right after the selected question
                state.questions.splice(question.orderIndex, 0, question)

                // Only update orderIndex for questions after the duplicated question
                state.questions.forEach((q, i) => {
                    if (i > index) {
                        q.orderIndex = i
                    }
                })

                state.selectedQuestionId = question.questionId
                state.currentQuestionIndex = index + 1
            }
        },

        // Select question
        selectQuestion: (state, action: PayloadAction<string>) => {
            const index = state.questions.findIndex(
                (q) => q.questionId === action.payload
            )
            if (index !== -1) {
                state.selectedQuestionId = action.payload
                state.currentQuestionIndex = index
            }
        },

        addQuestionPath: (
            state,
            action: PayloadAction<{
                questionId: string
                fromQuestionId: string | null
            }>
        ) => {
            state.questionPath[action.payload.questionId] =
                action.payload.fromQuestionId
        },

        // Reorder questions
        reorderQuestions: (
            state,
            action: PayloadAction<{ questions: Question[] }>
        ) => {
            const { questions } = action.payload
            state.questions = questions
        },
        updateRules: (state, action: PayloadAction<{ rules: any[] }>) => {
            state.surveyRules = action.payload.rules
        },

        updateRule: (
            state,
            action: PayloadAction<{
                questionId: string
                ruleIndex: number
                rule: {
                    condition: {
                        operator: 'CONTAINS' | 'NOT_CONTAINS'
                        value: string[]
                    }
                    action: {
                        type: 'SHOW'
                        questionIds: string[]
                    }
                }
            }>
        ) => {
            const { questionId, ruleIndex, rule } = action.payload
            const question = state.questions.find(
                (q) => q.questionId === questionId
            )
            if (question) {
                question.logic.rules[ruleIndex] = rule
            }
        },
        deleteRule: (
            state,
            action: PayloadAction<{ questionId: string; ruleIndex: number }>
        ) => {
            const { questionId, ruleIndex } = action.payload
            const question = state.questions.find(
                (q) => q.questionId === questionId
            )
            if (question) {
                question.logic.rules.splice(ruleIndex, 1)
            }
        },

        // Update preview response
        updatePreviewResponse: (
            state,
            action: PayloadAction<{
                questionId: string
                response: {
                    optionId?: string
                    optionIds?: string[]
                    otherValue?: string
                    value?: any
                }
            }>
        ) => {
            const { questionId, response } = action.payload
            state.previewResponses[questionId] = response
        },

        // Clear preview responses
        clearPreviewResponses: (state) => {
            state.previewResponses = {}
        },

        // Update a single question
        updateQuestion: (
            state,
            action: PayloadAction<{ question: Question }>
        ) => {
            const { question } = action.payload
            const index = state.questions.findIndex(
                (q) => q.questionId === question.questionId
            )
            if (index !== -1) {
                state.questions[index] = question
            }
        },
        updateQuestionRequired: (
            state,
            action: PayloadAction<{
                questionId: string
                isRequired: boolean
            }>
        ) => {
            const { questionId, isRequired } = action.payload
            const questionIndex = state.questions.findIndex(
                (q) => q.questionId === questionId
            )

            if (questionIndex !== -1) {
                state.questions[questionIndex].isRequired = isRequired
            }
        },
        setNewQuestionLoading: (state, action: PayloadAction<boolean>) => {
            state.newQuestionLoading = action.payload
        },
        // Update inline image for a question
        updateInlineImage: (
            state,
            action: PayloadAction<{
                questionId: string
                inlineImage: InlineImage | null
            }>
        ) => {
            const question = state.questions.find(
                (q) => q.questionId === action.payload.questionId
            )
            if (question) {
                if (action.payload.inlineImage === null) {
                    // Remove inline image by setting to null
                    question.inlineImage = null
                } else {
                    // Update or add inline image
                    question.inlineImage = action.payload.inlineImage
                }
            }
        },
        updateContentBlock: (
            state,
            action: PayloadAction<{ questionId: string; contentBlock: any }>
        ) => {
            const question = state.questions.find(
                (q) => q.questionId === action.payload.questionId
            )
            if (question && question.contentBlocks) {
                question.contentBlocks.blocks = action.payload.contentBlock
            }
        },

        // Update a specific property within a content block's content
        updateContentBlockProperty: (
            state,
            action: PayloadAction<{
                questionId: string
                blockId: string
                contentProperty: string
                value: string
            }>
        ) => {
            const { questionId, blockId, contentProperty, value } =
                action.payload
            const question = state.questions.find(
                (q) => q.questionId === questionId
            )

            if (question && question.contentBlocks) {
                const block = question.contentBlocks.blocks.find(
                    (b) => b.blockId === blockId
                )

                if (block) {
                    // Update only the specific property in the block's content
                    block.content = {
                        ...block.content,
                        [contentProperty]: value,
                    }
                }
            }
        },

        // Update multiple properties within a content block's content at once
        updateContentBlockProperties: (
            state,
            action: PayloadAction<{
                questionId: string
                blockId: string
                updates: Record<string, string>
            }>
        ) => {
            const { questionId, blockId, updates } = action.payload
            const question = state.questions.find(
                (q) => q.questionId === questionId
            )

            if (question && question.contentBlocks) {
                const block = question.contentBlocks.blocks.find(
                    (b) => b.blockId === blockId
                )

                if (block) {
                    // Update multiple properties in the block's content at once
                    block.content = {
                        ...block.content,
                        ...updates,
                    }
                }
            }
        },

        // Update all survey rules at once
        updateAllSurveyRules: (state, action: PayloadAction<any[]>) => {
            state.surveyRules = action.payload
        },

        // Update channel data with new format
        updateChannelData: (
            state,
            action: PayloadAction<{
                channelData: ChannelConfig
            }>
        ) => {
            state.channelData = action.payload.channelData
        },

        // Update welcome card
        updateWelcomeCard: (state, action: PayloadAction<CardData | null>) => {
            state.welcomeCard = action.payload
        },

        // Update thank you card
        updateThankYouCard: (state, action: PayloadAction<CardData | null>) => {
            state.thankYouCard = action.payload
        },

        // Update welcome card message type
        updateWelcomeCardMessageType: (
            state,
            action: PayloadAction<'default' | 'custom'>
        ) => {
            if (state.welcomeCard && action.payload === 'default') {
                state.welcomeCard = null
            } else if (state.welcomeCard && action.payload === 'custom') {
                state.welcomeCard = {
                    ...state.welcomeCard,
                    messageType: action.payload,
                }
            }
        },

        // Update thank you card message type
        updateThankYouCardMessageType: (
            state,
            action: PayloadAction<'default' | 'custom'>
        ) => {
            if (state.thankYouCard && action.payload === 'default') {
                state.thankYouCard = null
            } else if (state.thankYouCard && action.payload === 'custom') {
                state.thankYouCard = {
                    ...state.thankYouCard,
                    messageType: action.payload,
                }
            }
        },

        // Update public token
        updatePublicToken: (state, action: PayloadAction<string | null>) => {
            state.publicToken = action.payload
        },

        // Update QR code URL
        updateQrCodeUrl: (state, action: PayloadAction<string | null>) => {
            state.qrCodeUrl = action.payload
        },

        // Update WhatsApp preview state (template + variables)
        setWhatsAppPreview: (
            state,
            action: PayloadAction<{
                selectedTemplate: any | null
                variableMapping: Record<number, string>
                availableVariables: Array<{ key: string; label: string; example: string }>
            }>
        ) => {
            state.whatsAppPreview = action.payload
        },

        // Convert question type (replace question with API response)
        convertQuestionType: (
            state,
            action: PayloadAction<{ question: Question }>
        ) => {
            const { question } = action.payload
            const index = state.questions.findIndex(
                (q) => q.questionId === question.questionId
            )
            if (index !== -1) {
                state.questions[index] = question
            }
        },
    },
    extraReducers: (builder) => {
        // Reset builder state when user logs out or switches workspace
        builder
            .addCase(logoutUser.fulfilled, () => initialState)
            .addCase(logoutUser.rejected, () => initialState)
            .addCase(switchContext.fulfilled, () => initialState)
    },
})
export const {
    resetBuilder,
    updateSurveyId,
    updateSurveyName,
    updateSurveyTitle,
    updateSurveyDescription,
    setTemplateMode,
    clearTemplateMode,
    updateSurveyDetails,
    updateQuestions,
    addQuestion,
    updateQuestionText,
    updateQuestionDescription,
    updateQuestionConfig,
    deleteQuestion,
    duplicateQuestion,
    selectQuestion,
    reorderQuestions,
    addQuestionPath,
    updateInlineImage,
    updateRules,
    updateRule,
    deleteRule,
    updatePreviewResponse,
    clearPreviewResponses,
    updateQuestion,
    setNewQuestionLoading,
    updateContentBlock,
    updateContentBlockProperty,
    updateContentBlockProperties,
    updateAllSurveyRules,
    updateChannelData,
    updateWelcomeCard,
    updateThankYouCard,
    updateWelcomeCardMessageType,
    updateThankYouCardMessageType,
    updatePublicToken,
    updateQrCodeUrl,
    enableFlow,
    updateQuestionRequired,
    updateSurveyLayoutState,
    setWhatsAppPreview,
    convertQuestionType,
} = builderSlice.actions

export default builderSlice.reducer
